// 3D axes (Year 8, wording approved by the owner, 2 Nov 2026). An `axes_3d`
// is three axes, x, y and z, drawn on a slant like the Year 7 cube stacks,
// meeting at O and marked 1 to `ticks` on each. A box (cuboid) is drawn from
// O with its edges along the axes: `box` gives its length along x, along y
// and up z. Some of its corners carry a letter: `corners` maps each letter
// to the corner's three coordinates, each 0 or the box's length along that
// axis. Coordinates are never written on the picture.
//
// The screen reader says where each lettered corner is in plain words built
// from its coordinates (the owner chose to read positions, 2 Nov 2026):
//
//   (a, 0, 0)  on the x axis
//   (0, b, 0)  on the y axis
//   (0, 0, c)  on the z axis
//   (a, b, 0)  on the floor, in the corner furthest from O
//   (a, 0, c)  on the top of the box, straight above the corner on the x axis
//   (0, b, c)  on the top of the box, straight above the corner on the y axis
//   (a, b, c)  the corner of the box furthest from O
//
// O itself is never lettered again.
//
// Pure parsing and geometry only, no components (see
// practice-diagram-data.ts).

export type Axes3dCorner = {
  letter: string;
  at: [number, number, number];
};

export type Axes3dSpec = {
  type: 'axes_3d';
  // Each axis is marked 1 to ticks.
  ticks: number;
  // Along x, along y, up z.
  box: [number, number, number];
  corners: Axes3dCorner[];
};

const TICKS = { least: 2, most: 8 };
const LETTER = /^[A-Z]$/;

function isWhole(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value);
}

export function readAxes3d(record: Record<string, unknown>): Axes3dSpec | null {
  const { ticks, box, corners } = record;
  if (!isWhole(ticks) || ticks < TICKS.least || ticks > TICKS.most) return null;
  if (!Array.isArray(box) || box.length !== 3) return null;
  const sizes: number[] = [];
  for (const size of box as unknown[]) {
    // The box fits on the marked part of each axis.
    if (!isWhole(size) || size < 1 || size > ticks) return null;
    sizes.push(size);
  }
  const [a = 0, b = 0, c = 0] = sizes;
  const lettered: Axes3dCorner[] = [];
  if (corners !== undefined) {
    if (typeof corners !== 'object' || corners === null) return null;
    if (Array.isArray(corners)) return null;
    for (const [letter, at] of Object.entries(corners)) {
      if (!LETTER.test(letter) || letter === 'O') return null;
      if (!Array.isArray(at) || at.length !== 3) return null;
      const [x, y, z] = at as unknown[];
      // Each coordinate is 0 or the far side of the box.
      if (x !== 0 && x !== a) return null;
      if (y !== 0 && y !== b) return null;
      if (z !== 0 && z !== c) return null;
      if (x === 0 && y === 0 && z === 0) return null;
      const place: [number, number, number] = [x, y, z];
      if (lettered.some((corner) => corner.at.join() === place.join()))
        return null;
      lettered.push({ letter, at: place });
    }
  }
  const spec: Axes3dSpec = {
    type: 'axes_3d',
    ticks,
    box: [a, b, c],
    corners: lettered,
  };
  // Every letter must find a clear place next to its corner.
  return axes3dPicture(spec).crowded ? null : spec;
}

// Where a lettered corner is, in words; never its coordinates.
export function spokenCorner(at: [number, number, number]): string {
  const [x, y, z] = at.map((value) => value !== 0);
  if (x && y && z) return 'is the corner of the box furthest from O';
  if (z) {
    if (x)
      return 'is on the top of the box, straight above the corner on the x axis';
    if (y)
      return 'is on the top of the box, straight above the corner on the y axis';
    return 'is on the z axis';
  }
  if (x && y) return 'is on the floor, in the corner furthest from O';
  return x ? 'is on the x axis' : 'is on the y axis';
}

export function describeAxes3d(axes: Axes3dSpec): string {
  const [a, b, c] = axes.box;
  return [
    `Three axes, x, y and z, meeting at O, each marked 1 to ${String(axes.ticks)}.`,
    `A box is drawn from O: ${String(a)} along x, ${String(b)} along y and ${String(c)} up z.`,
    ...axes.corners.map(
      (corner) => `Corner ${corner.letter} ${spokenCorner(corner.at)}.`,
    ),
  ].join(' ');
}

// ---------------------------------------------------------------------------
// Where everything is drawn, in units close to one pixel each at the
// picture's natural width. O is at 0, 0.

export type AxesPoint = { x: number; y: number };
export type AxesText = AxesPoint & {
  text: string;
  part: 'tick' | 'axis' | 'origin' | 'corner';
};
export type AxesSegment = { from: AxesPoint; to: AxesPoint };

export type Axes3dPicture = {
  // Each axis: the part inside the box (hidden behind it, drawn dashed) and
  // the part beyond it.
  axes: { name: 'x' | 'y' | 'z'; hidden: AxesSegment; shown: AxesSegment }[];
  ticks: AxesSegment[];
  // The three faces seen from the front, the right and above.
  faces: { side: 'top' | 'left' | 'right'; points: AxesPoint[] }[];
  // The box's 9 edges that can be seen.
  edges: AxesSegment[];
  dots: AxesPoint[];
  texts: AxesText[];
  // A letter found no clear place near its corner: the reader refuses it.
  crowded: boolean;
  box: { x: number; y: number; width: number; height: number };
};

// True isometric, as the cube stacks: x runs down to the left, y down to the
// right, both at 30 degrees to the flat, and z straight up.
// One mark along an axis: bigger when there are fewer marks, so a small box
// still has room for its letters.
const UNIT = { most: 36, room: 190 };
const COS = Math.cos(Math.PI / 6);
const SIN = Math.sin(Math.PI / 6);
export const AXES_TEXT_SIZE = 14;
const LETTER_WIDTH = 8.4;
const TEXT_HEIGHT = 15;
// How far past the last mark each axis runs, and where its name sits.
const BEYOND = { line: 0.7, name: 1.3 };
const TICK = 4;
// From a mark to its number: straight below for x and y, up and to the
// left for z (clear of the box's edges at the marks they meet).
const NUMBER = 15;
const LETTER_DISTANCE = 14;
const PAD = 6;

export function unitFor(ticks: number): number {
  return Math.min(UNIT.most, UNIT.room / (ticks + BEYOND.name));
}

export function project(
  x: number,
  y: number,
  z: number,
  unit: number,
): AxesPoint {
  return {
    x: (y - x) * COS * unit,
    y: ((x + y) * SIN - z) * unit,
  };
}

function widthOf(text: string): number {
  return text.length * LETTER_WIDTH;
}

type Rect = { left: number; right: number; top: number; bottom: number };

function rectOf(text: AxesText): Rect {
  const width = widthOf(text.text);
  return {
    left: text.x - width / 2,
    right: text.x + width / 2,
    top: text.y - TEXT_HEIGHT / 2,
    bottom: text.y + TEXT_HEIGHT / 2,
  };
}

function overlaps(one: Rect, two: Rect): boolean {
  return (
    one.left < two.right &&
    two.left < one.right &&
    one.top < two.bottom &&
    two.top < one.bottom
  );
}

// Does the line cross the rectangle? Checked along its length.
function crosses(segment: AxesSegment, rect: Rect): boolean {
  const { from, to } = segment;
  const steps = Math.max(
    1,
    Math.ceil(Math.hypot(to.x - from.x, to.y - from.y)),
  );
  for (let step = 0; step <= steps; step += 1) {
    const x = from.x + ((to.x - from.x) * step) / steps;
    const y = from.y + ((to.y - from.y) * step) / steps;
    if (x > rect.left && x < rect.right && y > rect.top && y < rect.bottom)
      return true;
  }
  return false;
}

function grow(rect: Rect, by: number): Rect {
  return {
    left: rect.left - by,
    right: rect.right + by,
    top: rect.top - by,
    bottom: rect.bottom + by,
  };
}

export function axes3dPicture(axes: Axes3dSpec): Axes3dPicture {
  const [a, b, c] = axes.box;
  const n = axes.ticks;
  const unit = unitFor(n);
  const O = project(0, 0, 0, unit);
  const along = (axis: number, distance: number) =>
    project(
      axis === 0 ? distance : 0,
      axis === 1 ? distance : 0,
      axis === 2 ? distance : 0,
      unit,
    );
  const names = ['x', 'y', 'z'] as const;
  const pictureAxes = names.map((name, axis) => ({
    name,
    hidden: { from: O, to: along(axis, axes.box[axis] ?? 0) },
    shown: {
      from: along(axis, axes.box[axis] ?? 0),
      to: along(axis, n + BEYOND.line),
    },
  }));
  const ticks: AxesSegment[] = [];
  const texts: AxesText[] = [];
  names.forEach((name, axis) => {
    for (let mark = 1; mark <= n; mark += 1) {
      const at = along(axis, mark);
      // Across the axis: flat for z, upright for x and y.
      const across = axis === 2 ? { x: TICK, y: 0 } : { x: 0, y: TICK };
      ticks.push({
        from: { x: at.x - across.x, y: at.y - across.y },
        to: { x: at.x + across.x, y: at.y + across.y },
      });
      const offset =
        axis === 2
          ? { x: -COS * NUMBER, y: -SIN * NUMBER }
          : { x: 0, y: NUMBER };
      texts.push({
        x: at.x + offset.x,
        y: at.y + offset.y,
        text: String(mark),
        part: 'tick',
      });
    }
    const end = along(axis, n + BEYOND.name);
    texts.push({ ...end, text: name, part: 'axis' });
  });
  texts.push({ x: O.x, y: O.y + NUMBER, text: 'O', part: 'origin' });
  const corner = (x: number, y: number, z: number) =>
    project(x ? a : 0, y ? b : 0, z ? c : 0, unit);
  const faces: Axes3dPicture['faces'] = [
    {
      side: 'top',
      points: [
        corner(0, 0, 1),
        corner(1, 0, 1),
        corner(1, 1, 1),
        corner(0, 1, 1),
      ],
    },
    {
      side: 'left',
      points: [
        corner(1, 0, 0),
        corner(1, 1, 0),
        corner(1, 1, 1),
        corner(1, 0, 1),
      ],
    },
    {
      side: 'right',
      points: [
        corner(0, 1, 0),
        corner(1, 1, 0),
        corner(1, 1, 1),
        corner(0, 1, 1),
      ],
    },
  ];
  // Every edge but the three at O, which lie along the axes.
  const edges: AxesSegment[] = [
    [corner(1, 0, 0), corner(1, 1, 0)],
    [corner(0, 1, 0), corner(1, 1, 0)],
    [corner(1, 0, 0), corner(1, 0, 1)],
    [corner(0, 1, 0), corner(0, 1, 1)],
    [corner(1, 1, 0), corner(1, 1, 1)],
    [corner(0, 0, 1), corner(1, 0, 1)],
    [corner(0, 0, 1), corner(0, 1, 1)],
    [corner(1, 0, 1), corner(1, 1, 1)],
    [corner(0, 1, 1), corner(1, 1, 1)],
  ].map(([from, to]) => ({ from: from ?? O, to: to ?? O }));
  // Each corner's letter goes in the first clear place round it, trying
  // first the way that points out from the middle of the box.
  const middle = project(a / 2, b / 2, c / 2, unit);
  const lines = [
    ...pictureAxes.flatMap((axis) => [axis.hidden, axis.shown]),
    ...ticks,
    ...edges,
  ];
  const dots = axes.corners.map((corner) => project(...corner.at, unit));
  // A letter must sit nearer its own corner than any other corner of the
  // box, so it is never read as naming the wrong one.
  const allCorners = [0, 1].flatMap((x) =>
    [0, 1].flatMap((y) => [0, 1].map((z) => corner(x, y, z))),
  );
  let crowded = false;
  for (const [index, { letter }] of axes.corners.entries()) {
    const point = dots[index] ?? O;
    const out = Math.atan2(point.y - middle.y, point.x - middle.x);
    let placed: AxesText | null = null;
    for (
      let distance = LETTER_DISTANCE;
      !placed && distance <= 22;
      distance += 4
    )
      for (let turn = 0; turn <= 12 && !placed; turn += 1)
        for (const side of turn === 0 ? [1] : [1, -1]) {
          const angle = out + (side * turn * Math.PI) / 12;
          const text: AxesText = {
            x: point.x + Math.cos(angle) * distance,
            y: point.y + Math.sin(angle) * distance,
            text: letter,
            part: 'corner',
          };
          const rect = grow(rectOf(text), 1);
          const nearest = Math.min(
            ...allCorners.map((other) =>
              Math.hypot(other.x - text.x, other.y - text.y),
            ),
          );
          if (
            lines.some((line) => crosses(line, rect)) ||
            texts.some((other) => overlaps(rect, rectOf(other))) ||
            nearest < distance - 0.01
          )
            continue;
          placed = text;
          break;
        }
    if (!placed) crowded = true;
    texts.push(
      placed ?? {
        x: point.x + Math.cos(out) * LETTER_DISTANCE,
        y: point.y + Math.sin(out) * LETTER_DISTANCE,
        text: letter,
        part: 'corner',
      },
    );
  }
  const xs: number[] = [];
  const ys: number[] = [];
  for (const line of lines) xs.push(line.from.x, line.to.x);
  for (const line of lines) ys.push(line.from.y, line.to.y);
  for (const text of texts) {
    const rect = rectOf(text);
    xs.push(rect.left, rect.right);
    ys.push(rect.top, rect.bottom);
  }
  const x = Math.min(...xs) - PAD;
  const y = Math.min(...ys) - PAD;
  return {
    axes: pictureAxes,
    ticks,
    faces,
    edges,
    dots,
    texts,
    crowded,
    box: {
      x,
      y,
      width: Math.max(...xs) + PAD - x,
      height: Math.max(...ys) + PAD - y,
    },
  };
}
